import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Activity, Bed, Bone, HeartPulse, ArrowDown, CheckCircle2 } from "lucide-react";

const floatingIcons = [
  { Icon: Activity, top: "8%", left: "4%", delay: 0, label: "Stretching", color: "text-emerald-500", bg: "bg-emerald-50" },
  { Icon: Bone, top: "20%", right: "2%", delay: 0.6, label: "Spine Health", color: "text-blue-700", bg: "bg-blue-50" },
  { Icon: HeartPulse, bottom: "30%", left: "0%", delay: 1.2, label: "Pain Relief", color: "text-orange-500", bg: "bg-orange-50" },
  { Icon: Bed, bottom: "10%", right: "6%", delay: 1.8, label: "Better Sleep", color: "text-blue-700", bg: "bg-blue-50" },
];

export default function Hero({ onCTAClick, doctorImage, spineImage }) {
  return (
    <section
      id="hero"
      className="relative pt-28 pb-16 md:pt-32 md:pb-24 overflow-hidden hero-bg"
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* LEFT - Copy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-left"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 border border-emerald-100 mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">
                Doctor Approved
              </span>
            </div>

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#1E3A8A] leading-[1.05] tracking-tight"
              style={{ fontFamily: "Outfit" }}
              data-testid="hero-headline"
            >
              Relieve Back Pain{" "}
              <span className="relative inline-block">
                <span className="relative z-10">Naturally</span>
                <span className="absolute -bottom-1 left-0 right-0 h-3 bg-emerald-300/60 -z-0 rounded-sm" />
              </span>
              <br />
              in <span className="text-emerald-600">7 Days</span>
            </h1>

            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
              Doctor-approved stretches, posture fixes, sleeping positions, and daily routines
              to reduce lower back pain — safely from the comfort of your home.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Button
                onClick={onCTAClick}
                className="h-14 px-8 rounded-full bg-[#F97316] hover:bg-[#EA580C] text-white text-base font-bold cta-glow transition-all hover:-translate-y-0.5"
                data-testid="hero-cta-button"
              >
                Download PDF – ₹199
              </Button>
              <a
                href="#whats-inside"
                className="h-14 px-6 rounded-full border-2 border-slate-200 text-slate-700 font-semibold flex items-center justify-center hover:border-[#1E3A8A] hover:text-[#1E3A8A] transition-all"
                data-testid="hero-explore-link"
              >
                See What's Inside
                <ArrowDown className="w-4 h-4 ml-2" />
              </a>
            </div>

            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
              {[
                "Trusted by 5,000+ patients",
                "Easy home remedies",
                "Instant PDF access",
              ].map((t) => (
                <div key={t} className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span className="text-sm text-slate-700 font-medium">{t}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* RIGHT - Doctor visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="relative h-[440px] sm:h-[520px] lg:h-[600px]"
            data-testid="hero-visual"
          >
            {/* Background blob */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-[85%] h-[85%] rounded-full bg-gradient-to-br from-blue-50 via-emerald-50 to-orange-50 blur-2xl opacity-80" />
            </div>

            {/* Doctor image with mask */}
            <div className="relative h-full w-full flex items-center justify-center">
              <div className="relative w-[78%] aspect-[3/4] max-w-[420px] rounded-[3rem] overflow-hidden bg-blue-50 shadow-2xl shadow-blue-900/20">
                <img
                  src={doctorImage}
                  alt="Dr. Akshay Dhanda - Orthopedic Surgeon"
                  className="w-full h-full object-cover"
                  data-testid="hero-doctor-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/30 via-transparent to-transparent" />
              </div>

              {/* Floating icon badges */}
              {floatingIcons.map(({ Icon, top, bottom, left, right, delay, label, color, bg }, idx) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 + idx * 0.15 }}
                  className={`absolute glass rounded-2xl px-3 py-2 sm:px-4 sm:py-3 flex items-center gap-2 animate-float`}
                  style={{ top, bottom, left, right, animationDelay: `${delay}s` }}
                >
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl ${bg} flex items-center justify-center`}>
                    <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${color}`} strokeWidth={2.2} />
                  </div>
                  <span className="text-xs sm:text-sm font-semibold text-slate-800 whitespace-nowrap">{label}</span>
                </motion.div>
              ))}

              {/* Stat badge - 5000+ patients */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.1 }}
                className="absolute top-1/2 -right-2 sm:right-4 -translate-y-1/2 glass rounded-2xl p-4 shadow-xl"
              >
                <div className="text-2xl sm:text-3xl font-black text-[#1E3A8A]" style={{ fontFamily: "Outfit" }}>
                  5,000+
                </div>
                <div className="text-[10px] sm:text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Happy Patients
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
